/**
 * @file
 * Base JavaScript functionality for pseudo filter elements.
 *
 * This script provides the core functionality and base classes that are
 * extended by plugin-specific implementations.
 *
 * @param {object} $ - jQuery.
 * @param {object} Drupal - The Drupal object.
 * @param {Function} once - The once function.
 * @param {Array} dataLayer - The GTM dataLayer array.
 */

(function ($, Drupal, once, dataLayer) {
  'use strict';

  /**
   * Global toggle for auto-submit functionality.
   * Set to false to disable automatic form submission on filter changes.
   * Control via browser console: window.tnViewsPseudoFilterAutoSubmit = false/true
   */
  window.tnViewsPseudoFilterAutoSubmit = true;

  /**
   * Suppress the disruptive scroll jump on pseudo-filter AJAX updates.
   *
   * Drupal core's Views AJAX response includes a `scrollTop` command that calls
   * `element.scrollIntoView()` on the view container after every refresh. For
   * views enhanced with pseudo filters this causes a jarring page "jump" each
   * time a filter is applied or removed. We wrap the core command so that it is
   * skipped when the target is a pseudo-filter view, while preserving the
   * default scroll-to-top behaviour for all other views (e.g. pager clicks).
   */
  if (
    Drupal.AjaxCommands &&
    Drupal.AjaxCommands.prototype &&
    !Drupal.AjaxCommands.prototype.scrollTop.tnViewsPatched
  ) {
    const originalScrollTop = Drupal.AjaxCommands.prototype.scrollTop;

    Drupal.AjaxCommands.prototype.scrollTop = function (ajax, response) {
      const target = response && response.selector
        ? document.querySelector(response.selector)
        : null;

      if (target && target.classList.contains('view-with-pseudo-filters')) {
        return;
      }

      originalScrollTop.call(this, ajax, response);
    };

    Drupal.AjaxCommands.prototype.scrollTop.tnViewsPatched = true;
  }

  /**
   * Plugin registry for pseudo filter handlers.
   */
  const PLUGIN_REGISTRY = {
    taxonomy_pseudo_filter: () => window.TaxonomyPseudoFilterHandler,
    mirrored_taxonomy_pseudo_filter: () => window.TaxonomyPseudoFilterHandler,
    persistent_pseudo_filters: () => window.PersistentPseudoFilterHandler,
    node_pseudo_filter: () => window.NodePseudoFilterHandler,
    single_node_pseudo_filter: () => window.NodePseudoFilterHandler
  };

  /**
   * DOM utility helper class.
   */
  class DOMUtils {

    /**
     * Find the view container for a given wrapper element.
     *
     * @param {HTMLElement} wrapper - The wrapper element.
     * @return {HTMLElement|null} The view container element or null.
     */
    static findViewContainer(wrapper) {
      // Direct parent
      let viewContainer = wrapper.closest('.view');
      if (viewContainer) {
        return viewContainer;
      }

      // Try sibling
      const parentWrapper = wrapper.closest('.pseudo-filters-wrapper');
      if (parentWrapper) {
        viewContainer = parentWrapper.nextElementSibling;
        if (viewContainer && viewContainer.classList.contains('view')) {
          return viewContainer;
        }
      }

      // Fall back to any view with js-view-dom-id class
      return document.querySelector('.view[class*="js-view-dom-id-"]');
    }

    /**
     * Find the exposed form for a view.
     *
     * @param {HTMLElement} wrapper - The wrapper element.
     * @param {string} viewInstance - The view instance identifier.
     * @return {HTMLElement|null} The exposed form element or null.
     */
    static findExposedForm(wrapper, viewInstance) {
      if (viewInstance) {
        return document.querySelector(`form.views-exposed-form[data-view-instance="${viewInstance}"]`);
      }

      // Try to find form by view association
      const viewContainer = this.findViewContainer(wrapper);
      if (viewContainer) {
        const viewDomClass = Array.from(viewContainer.classList)
          .find(cls => cls.startsWith('js-view-dom-id-'));
        const viewDomId = viewDomClass ? viewDomClass.replace('js-view-dom-id-', '') : null;
        if (viewDomId) {
          return document.querySelector(`form[data-drupal-selector="views-exposed-form-${viewDomId}"]`) ||
                 document.querySelector(`form[id*="${viewDomId}"]`);
        }
      }

      return null;
    }

    /**
     * Find real filter element in form.
     *
     * @param {HTMLElement} form - The form element.
     * @param {string} filterId - The filter identifier.
     * @param {string} vocabulary - The vocabulary identifier.
     * @return {HTMLElement|null} The real filter element or null.
     */
    static findRealFilter(form, filterId, vocabulary) {
      if (!form) {
        return null;
      }

      // Try different selector patterns
      const selectors = [
        `#edit-${filterId}`,
        `[data-drupal-selector="edit-${filterId}"]`,
        `#edit-${filterId}-wrapper select`,
        `#edit-${filterId}-wrapper`
      ];

      if (vocabulary) {
        selectors.push(
          `#edit-${vocabulary}`,
          `[data-drupal-selector="edit-${vocabulary}"]`
        );
      }

      for (const selector of selectors) {
        const element = form.querySelector(selector);
        if (element) {
          return element.tagName === 'SELECT' ? element :
            element.querySelector('select') || element;
        }
      }

      return null;
    }
  }

  /**
   * Update visibility of clear filters link based on active filters.
   */
  function updateClearLinkVisibility() {
    // FIX: Use the correct selector - the element has class 'pseudo-filter-clear-link' not 'pseudo-filter-clear-wrapper'
    const clearLinks = document.querySelectorAll('.pseudo-filter-clear-link');
    clearLinks.forEach(function (wrapper) {
      let hasActiveFilters = false;

      // Check pseudo filter checkboxes (multi-select variants).
      const pseudoCheckboxes = document.querySelectorAll('.pseudo-filter-wrapper input[type="checkbox"]');
      pseudoCheckboxes.forEach(checkbox => {
        if (checkbox.checked) {
          hasActiveFilters = true;
        }
      });

      // Check pseudo filter radios (single-select variants). A checked radio
      // with a non-empty value represents an active filter; the empty value is
      // the "All" sentinel and should not count as active.
      const pseudoRadios = document.querySelectorAll('.pseudo-filter-wrapper input[type="radio"]:checked');
      pseudoRadios.forEach(radio => {
        if (radio.value !== '') {
          hasActiveFilters = true;
        }
      });

      // Show/hide the clear link based on active filters
      if (hasActiveFilters) {
        wrapper.style.display = 'inline-block';
      }
      else {
        wrapper.style.display = 'none';
      }
    });
  }

  // Expose so plugin files (which live in separate IIFE scopes) can update the
  // clear link visibility after syncing their own input state.
  window.tnViewsUpdateClearLinkVisibility = updateClearLinkVisibility;

  /**
   * Handle dispatch of "clear" CustomEvent. Also push the clear-filters
   * event to the DataLayer when isTracked param is set to true.
   *
   * @param {HTMLAnchorElement} clearFiltersLink - The "Clear Filters" link.
   * @param {boolean} isTracked - Boolean indicating when to push "clear-filters"
   *                              event to the dataLayer
   */
  function dispatchClearEvent(clearFiltersLink, isTracked) {
    const pseudoFiltersContainer = clearFiltersLink.parentElement;
    const eventDetail = isTracked ? 'start' : 'end';
    const clearEvent = new CustomEvent('clear', {bubbles: false, detail: eventDetail});
    pseudoFiltersContainer.dispatchEvent(clearEvent);

    if (isTracked) {
      dataLayer.push({
        'event': 'clear-filters',
        'data-analytics-filter-group': null,
        'data-analytics-filter-selection': clearFiltersLink.textContent
      });
    }
  }

  /**
   * Strip stale exposed filter parameters from a submit button's bound AJAX URL.
   *
   * Drupal core's Views AJAX bakes the current exposed filter querystring (e.g.
   * "area[]=571&...") into the submit button's Drupal.Ajax instance "url" when
   * the view is rendered. Because that frozen URL is used as the GET base by
   * jquery.form, clearing the live form fields alone is not enough: the stale
   * exposed values would be re-sent and the filters restored. This removes the
   * exposed filter params from the bound URL so the (now empty) live form drives
   * the request, while preserving framework params (viewsreference, _wrapper_format,
   * ajax_page_state, etc.).
   *
   * Exported as window.tnViewsStripStaleExposedParams because the search view's
   * own Clear Filters button (theme scope, view-search.js) submits the same
   * exposed forms and needs the identical treatment.
   *
   * @param {HTMLElement} submitButton - The exposed form submit button.
   */
  function stripStaleExposedParamsFromAjaxUrl(submitButton) {
    if (!submitButton || typeof Drupal === 'undefined' || !Drupal.ajax || !Drupal.ajax.instances) {
      return;
    }

    const isFrameworkParam = key =>
      key.charAt(0) === '_' ||
      key.indexOf('viewsreference') === 0 ||
      key.indexOf('ajax_page_state') === 0;

    const stripExposed = url => {
      if (!url) {
        return url;
      }
      const queryIndex = url.indexOf('?');
      if (queryIndex === -1) {
        return url;
      }
      const base = url.slice(0, queryIndex);
      const params = new URLSearchParams(url.slice(queryIndex + 1));
      const kept = new URLSearchParams();
      for (const [key, value] of params) {
        if (isFrameworkParam(key)) {
          kept.append(key, value);
        }
      }
      const queryString = kept.toString();
      return queryString ? `${base}?${queryString}` : base;
    };

    Drupal.ajax.instances.forEach(instance => {
      if (!instance || instance.element !== submitButton) {
        return;
      }
      if (instance.url) {
        instance.url = stripExposed(instance.url);
      }
      if (instance.options && instance.options.url) {
        instance.options.url = stripExposed(instance.options.url);
      }
    });
  }

  window.tnViewsStripStaleExposedParams = stripStaleExposedParamsFromAjaxUrl;

  /**
   * Handle clear filters link click.
   *
   * @param {Event} event - The click event.
   */
  function handleClearFiltersClick(event) {
    event.preventDefault();

    // Signal to all pseudo filter handlers (including those in separate IIFE
    // scopes) that a clear is in progress. While this flag is set, individual
    // input changes must not schedule their own auto-submit, otherwise the
    // per-input debounced submissions race against the single submit performed
    // at the end of this handler and a stale response can restore the filters.
    window.tnViewsPseudoFiltersClearing = true;

    // Sends clear event to pause filter tracking in "pseudo-filter-taxonomy.js" while filters
    // individually reset to their initial state. Also pushes "clear-filters" event to the dataLayer
    dispatchClearEvent(event.currentTarget, true);

    const viewInstance = this.dataset.viewInstance;

    // Find the exposed form
    let form = null;
    if (viewInstance) {
      form = document.querySelector(`form.views-exposed-form[data-view-instance="${viewInstance}"]`);
    }
    if (!form) {
      form = document.querySelector('form.views-exposed-form');
    }

    if (!form) {
      window.tnViewsPseudoFiltersClearing = false;
      return;
    }

    // Clear all pseudo filter checkboxes
    const pseudoCheckboxes = document.querySelectorAll('.pseudo-filter-wrapper input[type="checkbox"]');
    pseudoCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
      // Trigger change event to update the real filter
      const changeEvent = new Event('change', {bubbles: true});
      checkbox.dispatchEvent(changeEvent);
    });

    // Reset all pseudo filter radios (single-select variants) back to the
    // empty "All" sentinel so the filter no longer constrains results.
    const pseudoRadios = document.querySelectorAll('.pseudo-filter-wrapper input[type="radio"]');
    pseudoRadios.forEach(radio => {
      const shouldBeChecked = radio.value === '';
      if (radio.checked !== shouldBeChecked) {
        radio.checked = shouldBeChecked;
        const changeEvent = new Event('change', {bubbles: true});
        radio.dispatchEvent(changeEvent);
      }
    });

    // Clear all real filters
    const selects = form.querySelectorAll('select');
    selects.forEach(select => {
      if (select.multiple) {
        Array.from(select.options).forEach(option => {
          option.selected = false;
        });
      }
      else {
        select.value = select.options[0] ? select.options[0].value : '';
      }
    });

    // Clear text inputs
    const textInputs = form.querySelectorAll('input[type="text"], input[type="search"]');
    textInputs.forEach(input => {
      input.value = '';
    });

    // Clear checkboxes in the real form
    const realCheckboxes = form.querySelectorAll('.js-form-type-checkboxes input[type="checkbox"], .js-form-type-bef-checkboxes input[type="checkbox"]');
    realCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Submit the form to apply the reset
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    if (submitButton) {
      // Remove the stale exposed filter querystring frozen into the submit
      // button's bound AJAX URL so the cleared form drives the request instead
      // of re-applying the previously selected filters.
      stripStaleExposedParamsFromAjaxUrl(submitButton);
      submitButton.click();
    }

    // Send clear event to resume filter tracking in "pseudo-filter-taxonomy.js"
    dispatchClearEvent(event.currentTarget, false);

    // The single, authoritative submit above has been triggered, so clearing is
    // complete. Re-enable per-input auto-submit for subsequent interactions.
    window.tnViewsPseudoFiltersClearing = false;

    // Update clear link visibility after clearing
    updateClearLinkVisibility();
  }

  /**
   * Handle filter button toggle.
   *
   * @param {Event} event - The click event.
   */
  function handleFilterButtonToggle(event) {
    // Change the aria expanded attribute on the trigger
    const trigger = event.currentTarget;
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', !expanded ? 'true' : 'false');

    // If this button controls another element, find it and update related buttons
    const controlsId = trigger.getAttribute('aria-controls');
    if (controlsId) {
      // Find all other buttons that control the same element
      document.querySelectorAll(`.pseudo-filters-trigger[aria-controls="${controlsId}"]`).forEach(function (relatedButton) {
        if (relatedButton !== trigger) {
          relatedButton.setAttribute('aria-expanded', !expanded ? 'true' : 'false');
        }
      });
    }
  }

  /**
   * Behavior for pseudo filter handling.
   */
  // Store handlers globally so we can reconnect them after AJAX
  window.tnViewsPseudoFilterHandlers = window.tnViewsPseudoFilterHandlers || new Map();

  Drupal.behaviors.tnViewsPseudoFilters = {
    attach: function (context, settings) {
      // Update clear link visibility on initial load and after AJAX
      updateClearLinkVisibility();

      // Attach clear filters link handlers
      once('tn-pseudo-filters-clear', '.pseudo-filter-clear-link', context).forEach(function (clearLink) {
        clearLink.addEventListener('click', handleClearFiltersClick);
      });

      // Attach filter button handlers
      once('tn-pseudo-filters-trigger', '.pseudo-filters-trigger', context).forEach(function (trigger) {
        trigger.addEventListener('click', handleFilterButtonToggle);
      });

      // For AJAX updates, handle reconnection more efficiently
      if (context !== document) {
        // For AJAX updates, we need to check all existing handlers, not just those in context
        // because pseudo filters may be outside the AJAX-updated region
        window.tnViewsPseudoFilterHandlers.forEach(function (existingHandler, handlerId) {
          // Always reconnect after AJAX to ensure event listeners are fresh
          if (existingHandler && existingHandler.wrapper && existingHandler.reconnectAfterAjax) {
            existingHandler.reconnectAfterAjax();
          }
        });
      }

      // Process any new pseudo filters found in the context
      once('tn-pseudo-filters', '.pseudo-filter-wrapper', context).forEach(function (wrapper) {
        const handlerId = wrapper.dataset.filterId + '-' + (wrapper.dataset.viewInstance || 'default');

        // Skip if handler already exists (we already reconnected it above)
        if (window.tnViewsPseudoFilterHandlers.has(handlerId)) {
          return;
        }

        // Create handler using plugin registry
        const handlerSettings = settings.tnViewsPseudoFilters || settings.tnViews || {};
        let pluginType = wrapper.dataset.plugin;
        if (!pluginType) {
          if (wrapper.classList.contains('pseudo-filter-taxonomy')) {
            pluginType = 'taxonomy_pseudo_filter';
          }
          else if (wrapper.classList.contains('pseudo-filter-node')) {
            // Detect if single or multiple selection
            pluginType = wrapper.dataset.multiple === 'false' ? 'single_node_pseudo_filter' : 'node_pseudo_filter';
          }
          else if (wrapper.classList.contains('pseudo-filter-hierarchical-checkboxes')) {
            pluginType = 'pseudo_filter_hierarchical_checkboxes';
          }
          else {
            pluginType = 'default';
          }
        }
        const PluginFactory = PLUGIN_REGISTRY[pluginType];
        const PluginClass = (PluginFactory && PluginFactory()) || PseudoFilterHandler;
        const filterHandler = new PluginClass(wrapper, handlerSettings);

        filterHandler.initialize();

        // Store the handler
        window.tnViewsPseudoFilterHandlers.set(handlerId, filterHandler);
      });

      // Handle persistent pseudo filters for ViewsReference.
      if (settings.tnViews && settings.tnViews.persistentFilters) {
        Object.keys(settings.tnViews.persistentFilters).forEach(function (wrapperId) {
          const container = document.getElementById(wrapperId);
          if (container && once('tn-persistent-filters', `#${wrapperId}`, context).length) {
            const persistentSettings = settings.tnViews.persistentFilters[wrapperId];

            // Use PersistentPseudoFilterHandler if available
            const HandlerFactory = PLUGIN_REGISTRY['persistent_pseudo_filters'];
            const HandlerClass = (HandlerFactory && HandlerFactory()) || PseudoFilterHandler;
            const wrapper = container.querySelector('.pseudo-filter-wrapper') || container;
            const handler = new HandlerClass(wrapper, persistentSettings);

            handler.initialize();

            // Store the handler for reconnection after AJAX
            if (wrapper) {
              const handlerId = wrapper.dataset.filterId + '-' + (wrapper.dataset.viewInstance || 'default');
              window.tnViewsPseudoFilterHandlers.set(handlerId, handler);
            }
          }
        });
      }

      // Re-evaluate clear link visibility after handlers have initialized and
      // synced their inputs from the real filter. The early call above runs
      // before that sync, so on a page loaded with active filters (e.g. via URL
      // query parameters) the clear link would otherwise stay hidden.
      updateClearLinkVisibility();
    },

    detach: function (context, settings, trigger) {
      // Clean up if needed when elements are removed from DOM.
      if (trigger === 'unload') {
        // Clean up global handlers map to prevent memory leaks
        if (window.tnViewsPseudoFilterHandlers) {
          window.tnViewsPseudoFilterHandlers.forEach((handler, key) => {
            // Check if the handler's wrapper is within the context being removed
            if (handler && handler.wrapper && context.contains && context.contains(handler.wrapper)) {
              // Properly destroy the handler
              if (typeof handler.destroy === 'function') {
                handler.destroy();
              }
              // Remove from global map
              window.tnViewsPseudoFilterHandlers.delete(key);
            }
          });
        }

        once.remove('tn-pseudo-filters', '.pseudo-filter-wrapper', context);
        once.remove('tn-persistent-filters', '.persistent-pseudo-filters', context);
        once.remove('tn-pseudo-filters-clear', '.pseudo-filter-clear-link', context);
      }
    }
  };

  /**
   * PseudoFilterHandler base class.
   * Provides core functionality for all pseudo filter implementations.
   */
  class PseudoFilterHandler {

    /**
     * Constructor.
     *
     * @param {HTMLElement} wrapper - The wrapper element.
     * @param {Object} settings - The settings object.
     */
    constructor(wrapper, settings) {
      this.wrapper = wrapper;
      this.settings = settings;
      this.filterId = wrapper.dataset.filterId;
      this.vocabulary = wrapper.dataset.vocabulary;
      this.plugin = wrapper.dataset.plugin;
      this.realFilter = null;
      this.exposedForm = null;
      this.allCheckboxes = null;
      this.termMap = new Map();
      this.childToParentMap = new Map();
      this.viewName = wrapper.dataset.viewName || '';
      this.displayId = wrapper.dataset.displayId || '';
      // Use DOM utility to find view container
      const viewContainer = DOMUtils.findViewContainer(this.wrapper);
      this.isAjaxView = viewContainer && Array.from(viewContainer.classList).some(cls => cls.startsWith('js-view-dom-id-'));
      this.submitTimeout = null;
      this.debounceDelay = (settings && settings.autosubmitDelay) || 500; // Increased from 300ms
      this.boundHandleOutsideClick = null;
      this.hoverCloseTimer = null; // Timer for delayed close on hover out
      this.boundHandleWrapperKeydown = null;
    }

    /**
     * Initialize the handler.
     */
    initialize() {
      this.findRealFilter();
      this.findExposedForm();
      this.setupCheckboxes();
      this.attachEventListeners();

      // Debug logging (commented out for production)
    }

    /**
     * Find the corresponding hidden exposed filter.
     */
    findRealFilter() {
      const viewInstance = this.wrapper.dataset.viewInstance;

      // Try data attributes first
      if (viewInstance) {
        this.realFilter = document.querySelector(
          `[data-view-instance="${viewInstance}"][data-pseudo-filter="${this.filterId}"]`
        );
      }

      if (!this.realFilter) {
        this.realFilter = document.querySelector(`[data-pseudo-filter="${this.filterId}"]`);
      }

      // Use DOM utility to search within form
      if (!this.realFilter) {
        this.findExposedForm();
        this.realFilter = DOMUtils.findRealFilter(this.exposedForm, this.filterId, this.vocabulary);
      }

      if (!this.realFilter) {
        // Last resort: look for a form element with matching name in any exposed form
        const allExposedForms = document.querySelectorAll('form.views-exposed-form');
        allExposedForms.forEach(form => {
          if (!this.realFilter) {
            // Try SELECT first
            const selectElement = form.querySelector(`select[name="${this.filterId}"], select[name="${this.filterId}[]"]`);
            if (selectElement) {
              this.realFilter = selectElement;
              this.exposedForm = form;
            }
            else {
              // Try checkboxes
              const checkboxGroups = form.querySelectorAll('.js-form-type-checkboxes, .js-form-type-bef-checkboxes');
              checkboxGroups.forEach(group => {
                const checkbox = group.querySelector('input[type="checkbox"]');
                const fieldName = checkbox ? checkbox.name : null;
                if (fieldName && fieldName.replace(/\[\]$/, '') === this.filterId) {
                  this.realFilter = group;
                  this.exposedForm = form;
                }
              });
            }
          }
        });
      }
    }

    /**
     * Find the exposed form related to the pseudo filter.
     */
    findExposedForm() {
      if (this.exposedForm) {
        return;
      }

      // Use DOM utility to find exposed form
      this.exposedForm = DOMUtils.findExposedForm(this.wrapper, this.wrapper.dataset.viewInstance);
      // Fallback: find any exposed form on the page
      if (!this.exposedForm) {
        this.exposedForm = document.querySelector('form.views-exposed-form.pseudo-filters-form') ||
                          document.querySelector('form.views-exposed-form');
      }
    }

    /**
     * Set up checkbox management and mapping.
     */
    setupCheckboxes() {
      this.allCheckboxes = this.wrapper.querySelectorAll('input[type="checkbox"]');
      this.buildTermMappings();
      this.syncWithRealFilter();
      this.updateSummaryDisplay();
    }

    /**
     * Build term mappings for parent-child relationships.
     */
    buildTermMappings() {
      this.termMap.clear();
      this.childToParentMap.clear();

      // Build term map with ES6 destructuring
      this.allCheckboxes.forEach(checkbox => {
        const {value: termId, dataset: {parent: parentId}} = checkbox;

        this.termMap.set(termId, {
          id: termId,
          parentId: parentId || null,
          checkbox,
          children: []
        });

        if (parentId) {
          this.childToParentMap.set(termId, parentId);
        }
      });

      // Build children arrays
      this.termMap.forEach((term) => {
        if (term.parentId && this.termMap.has(term.parentId)) {
          this.termMap.get(term.parentId).children.push(term.id);
        }
      });
    }

    /**
     * Remove existing event listeners to prevent duplicates.
     */
    removeEventListeners() {
      // Remove checkbox change listeners
      if (this.allCheckboxes && this.boundHandleCheckboxChange) {
        this.allCheckboxes.forEach(checkbox => {
          checkbox.removeEventListener('change', this.boundHandleCheckboxChange);
        });
      }

      // Remove wrapper keydown listener
      if (this.boundHandleWrapperKeydown && this.wrapper) {
        this.wrapper.removeEventListener('keydown', this.boundHandleWrapperKeydown);
      }

      // Remove form submit listeners
      if (this.exposedForm && this.boundHandleFormSubmit) {
        if (this.isAjaxView) {
          $(this.exposedForm).off('submit', this.boundHandleFormSubmit);
        }
        else {
          this.exposedForm.removeEventListener('submit', this.boundHandleFormSubmit);
        }
      }

      // Remove outside click listener
      if (this.boundHandleOutsideClick) {
        document.removeEventListener('click', this.boundHandleOutsideClick);
      }

      // Remove toggle listener from details element
      const detailsElement = this.wrapper.querySelector('details.pseudo-filter-details');
      if (detailsElement) {
        // Note: We can't easily remove anonymous event listeners,
        // but they will be garbage collected when the element is replaced
      }
    }

    /**
     * Attach event listeners.
     */
    attachEventListeners() {
      // Remove any existing listeners first
      this.removeEventListeners();

      // Bind methods to maintain context - only bind if not already bound
      if (!this.boundHandleCheckboxChange) {
        this.boundHandleCheckboxChange = this.handleCheckboxChange.bind(this);
      }
      if (!this.boundHandleWrapperKeydown) {
        this.boundHandleWrapperKeydown = this.handleWrapperKeydown.bind(this);
      }
      if (!this.boundHandleFormSubmit) {
        this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      }
      if (!this.boundHandleOutsideClick) {
        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
      }

      // Listen to checkbox changes
      this.allCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', this.boundHandleCheckboxChange);
      });

      // Single delegated keydown handler for keyboard interactions.
      this.wrapper.addEventListener('keydown', this.boundHandleWrapperKeydown);

      // Listen to form submissions
      if (this.exposedForm) {
        // For AJAX views, sync before the AJAX submission
        if (this.isAjaxView) {
          $(this.exposedForm).on('submit', this.boundHandleFormSubmit);
        }
        else {
          this.exposedForm.addEventListener('submit', this.boundHandleFormSubmit);
        }
      }

      // Listen to details element open/close for outside click handling
      const detailsElement = this.wrapper.querySelector('details.pseudo-filter-details');
      if (detailsElement) {
        const summaryEl = detailsElement.querySelector('summary');

        // Update aria-expanded preemptively on click so screen readers
        // (e.g. NVDA+Chrome) receive the new state before the toggle fires.
        summaryEl.addEventListener('click', () => {
          summaryEl.setAttribute('aria-expanded', detailsElement.open ? 'false' : 'true');
        });

        detailsElement.addEventListener('toggle', () => {
          summaryEl.setAttribute('aria-expanded', detailsElement.open ? 'true' : 'false');
          if (detailsElement.open) {
            // Add outside click listener when dropdown opens
            setTimeout(() => {
              document.addEventListener('click', this.boundHandleOutsideClick);
            }, 0);
          }
          else {
            // Remove outside click listener when dropdown closes
            document.removeEventListener('click', this.boundHandleOutsideClick);
            // Clear any pending hover close timer
            if (this.hoverCloseTimer) {
              clearTimeout(this.hoverCloseTimer);
              this.hoverCloseTimer = null;
            }
          }
        });

        // Add hover event listeners for delayed close
        this.wrapper.addEventListener('mouseenter', () => {
          // Clear any pending close timer when hovering over the dropdown
          if (this.hoverCloseTimer) {
            clearTimeout(this.hoverCloseTimer);
            this.hoverCloseTimer = null;
          }
        });

        this.wrapper.addEventListener('mouseleave', () => {
          // Start a timer to close the dropdown after 1 second
          if (detailsElement.open) {
            this.hoverCloseTimer = setTimeout(() => {
              detailsElement.open = false;
              // Remove the outside click listener when closing
              document.removeEventListener('click', this.boundHandleOutsideClick);
            }, 1000); // 1 second delay
          }
        });
      }
    }

    /**
     * Handle clicks outside the dropdown to close it.
     *
     * @param {Event} event - The click event.
     */
    handleOutsideClick(event) {
      const detailsElement = this.wrapper.querySelector('details.pseudo-filter-details');
      if (!detailsElement || !detailsElement.open) {
        return;
      }

      // Check if the click was inside the dropdown
      if (!this.wrapper.contains(event.target)) {
        // Click was outside, close the dropdown
        detailsElement.open = false;
        // Remove the listener since dropdown is now closed
        document.removeEventListener('click', this.boundHandleOutsideClick);
      }
    }

    /**
     * Reconnect handler after AJAX update.
     */
    reconnectAfterAjax() {
      // Clear any pending submit timeout
      if (this.submitTimeout) {
        clearTimeout(this.submitTimeout);
        this.submitTimeout = null;
      }

      // Only re-initialize if the wrapper has actually been replaced
      // Check if our cached elements are still in the DOM
      const needsFullReinit = !this.realFilter || !this.realFilter.isConnected ||
                              !this.exposedForm || !this.exposedForm.isConnected;

      if (needsFullReinit) {
        // Remove existing event listeners first
        this.removeEventListeners();

        // Re-check if this is an AJAX view (DOM might have changed)
        let viewContainer = this.wrapper.closest('.view');
        if (!viewContainer) {
          // Try to find the view as a sibling or nearby element
          const parentWrapper = this.wrapper.closest('.pseudo-filters-wrapper');
          if (parentWrapper) {
            viewContainer = parentWrapper.nextElementSibling;
            if (!viewContainer || !viewContainer.classList.contains('view')) {
              // Look for any view with js-view-dom-id class
              viewContainer = document.querySelector('.view[class*="js-view-dom-id-"]');
            }
          }
        }
        this.isAjaxView = viewContainer && Array.from(viewContainer.classList).some(cls => cls.startsWith('js-view-dom-id-'));

        // Re-initialize everything
        this.findRealFilter();
        this.findExposedForm();
        this.setupCheckboxes();
        this.attachEventListeners();
      }
      // Otherwise just sync the current state
      else {
        this.syncWithRealFilter();
        this.updateSummaryDisplay();
      }

      // Debug logging (commented out for production)
    }

    /**
     * Handle checkbox state changes.
     *
     * @param {Event} event - The change event.
     */
    handleCheckboxChange(event) {
      const checkbox = event.target;
      const termId = checkbox.value;
      const isChecked = checkbox.checked;

      // Handle parent-child relationships if needed
      if (this.wrapper.classList.contains('pseudo-filter-parent-child') ||
          this.wrapper.classList.contains('pseudo-filter-hierarchical-checkboxes')) {
        this.handleParentChildSelection(termId, isChecked);
      }

      // Update the real filter
      const selectedTerms = this.getSelectedTermsWithChildren();
      this.updateRealFilter(selectedTerms);

      // Update summary display
      this.updateSummaryDisplay();

      // Update clear link visibility
      updateClearLinkVisibility();

      // Don't automatically close dropdown - let user make multiple selections
      // The dropdown will close when they click outside or hover away for 1 second

      // Auto-submit for AJAX views if configured. Skip while a clear is in
      // progress: the clear handler performs a single authoritative submit and
      // per-input submissions here would race against it.
      if (this.isAjaxView && this.exposedForm && window.tnViewsPseudoFilterAutoSubmit && !window.tnViewsPseudoFiltersClearing) {
        this.debouncedSubmit();
      }
      else if (this.isAjaxView && this.exposedForm && !window.tnViewsPseudoFilterAutoSubmit) {
        // Auto-submit is disabled
      }
    }

    /**
     * Ensure Enter key toggles checkbox selection.
     *
     * Native checkbox interaction uses Space; this provides parity with
     * the expected interaction pattern in dropdowns.
     *
     * @param {KeyboardEvent} event - The keydown event.
     */
    handleWrapperKeydown(event) {
      if (event.key !== 'Enter' || event.isComposing) {
        return;
      }

      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') {
        return;
      }

      event.preventDefault();
      target.click();
    }

    /**
     * Update the summary display text based on selected items.
     */
    updateSummaryDisplay() {
      // Find the summary element
      const summaryElement = this.wrapper.querySelector('details.pseudo-filter-details summary .summary-placeholder');
      if (!summaryElement) {
        return;
      }

      // Get all checked checkboxes
      const checkedBoxes = Array.from(this.wrapper.querySelectorAll('input[type="checkbox"]:checked'));
      const selectedCount = checkedBoxes.length;

      if (selectedCount === 0) {
        summaryElement.textContent = 'Select items';
      }
      else if (selectedCount <= 3) {
        // Show the names of selected items using map
        const selectedLabels = checkedBoxes
          .map(checkbox => {
            const label = this.wrapper.querySelector(`label[for="${checkbox.id}"]`);
            return label ? label.textContent.trim() : null;
          })
          .filter(Boolean);
        summaryElement.textContent = selectedLabels.join(', ');
      }
      else {
        // Show count when more than 3 items
        summaryElement.textContent = `${selectedCount} items selected`;
      }

      const summary = summaryElement.closest('summary');
      const filterLabelEl = this.wrapper.querySelector('.pseudo-filter-label');
      if (summary && filterLabelEl) {
        summary.setAttribute('aria-label', `${filterLabelEl.textContent.trim()}: ${summaryElement.textContent}`);
      }
    }

    /**
     * Close the dropdown details element.
     */
    closeDropdown() {
      // Find the details element within the wrapper
      const detailsElement = this.wrapper.querySelector('details.pseudo-filter-details');
      if (detailsElement && detailsElement.open) {
        // Add a small delay to allow the checkbox change to register visually
        setTimeout(() => {
          detailsElement.open = false;
        }, 100);
      }
    }

    /**
     * Debounced form submission.
     */
    debouncedSubmit() {

      // Clear any existing timeout
      if (this.submitTimeout) {
        clearTimeout(this.submitTimeout);
      }

      // Check if an AJAX request is already in progress
      const isProcessing = this.exposedForm && (
        this.exposedForm.classList.contains('views-ajax-processed') ||
        document.querySelector('.ajax-progress-throbber')
      );

      if (isProcessing) {
        // Queue another submission for after the current one completes
        this.submitTimeout = setTimeout(() => {
          this.debouncedSubmit();
        }, 100);
        return;
      }

      // Set a new timeout for form submission
      this.submitTimeout = setTimeout(() => {
        this.submitForm();
        // Don't auto-close dropdown after form submission
      }, this.debounceDelay);
    }

    /**
     * Submit the form to apply filter changes.
     */
    submitForm() {

      // Re-find the form in case it was replaced by AJAX
      this.findExposedForm();

      if (!this.exposedForm) {
        return;
      }

      // Check if form is still connected to the DOM
      if (!this.exposedForm.isConnected) {
        this.exposedForm = null;
        this.findExposedForm();

        if (!this.exposedForm) {
          return;
        }
      }

      // Look for the submit button (usually "Apply" or similar)
      const submitButton = this.exposedForm.querySelector('input[type="submit"], button[type="submit"]');

      if (submitButton) {
        // Check if an AJAX request is already in progress
        // by looking for the progress throbber or disabled state
        const isProcessing = this.exposedForm.classList.contains('views-ajax-processed') ||
                           submitButton.disabled ||
                           document.querySelector('.ajax-progress-throbber');

        if (isProcessing) {
          return;
        }

        // If there's a submit button, click it to trigger proper form submission
        // This ensures any form validation and AJAX handlers are properly triggered

        // Use a small delay to ensure the form values are properly set
        setTimeout(() => {
          if (submitButton.isConnected) {
            // Strip stale exposed-filter params from the AJAX URL bound to the
            // submit button. Drupal core's Views AJAX (ajax_view.js) bakes the
            // current querystring into the Drupal.ajax instance and its
            // "remove existing filters" filter only matches simple inputs by
            // name, so multi-value selects (e.g. <select name="area[]">) leave
            // stale area[] params in the URL. Without this, the new form state
            // is masked by the previous querystring and filters appear to not
            // update. See related fix in handleClearFiltersClick.
            stripStaleExposedParamsFromAjaxUrl(submitButton);
            submitButton.click();
          }
          else {
            // Button no longer connected
          }
        }, 10);
      }
      else {
        // Fallback: directly submit the form if no submit button found
        this.exposedForm.submit();
      }
    }

    /**
     * Handle parent-child selection logic.
     *
     * @param {string} termId - The term ID.
     * @param {boolean} isChecked - Whether the checkbox is checked.
     */
    handleParentChildSelection(termId, isChecked) {
      const term = this.termMap.get(termId);
      if (!term) {
        return;
      }

      // If checking a parent, check all children
      if (isChecked && term.children.length > 0) {
        term.children.forEach(childId => {
          const childTerm = this.termMap.get(childId);
          if (childTerm && childTerm.checkbox) {
            childTerm.checkbox.checked = true;
          }
        });
      }

      // If unchecking a child, uncheck parent if no other children are checked
      if (!isChecked && term.parentId) {
        const parent = this.termMap.get(term.parentId);
        if (parent) {
          const anyChildChecked = parent.children.some(childId => {
            const child = this.termMap.get(childId);
            return child && child.checkbox && child.checkbox.checked;
          });
          if (!anyChildChecked && parent.checkbox) {
            parent.checkbox.checked = false;
          }
        }
      }
    }

    /**
     * Get selected terms including their children.
     *
     * @return {Array} Array of selected term IDs.
     */
    getSelectedTermsWithChildren() {
      const selectedTerms = new Set();

      this.allCheckboxes.forEach(checkbox => {
        if (checkbox.checked) {
          selectedTerms.add(checkbox.value);
        }
      });

      return Array.from(selectedTerms);
    }

    /**
     * Update the real filter with selected values.
     *
     * @param {Array} selectedTerms - Array of selected term IDs.
     */
    updateRealFilter(selectedTerms) {
      if (!this.realFilter) {
        return;
      }


      // Check if real filter is a SELECT element
      if (this.realFilter.tagName === 'SELECT') {

        // Handle multi-select
        if (this.realFilter.multiple) {
          // Clear all selections first
          Array.from(this.realFilter.options).forEach(option => {
            option.selected = selectedTerms.includes(option.value);
          });
        }
        else {
          // Single select - use first selected term or empty
          const newValue = selectedTerms.length > 0 ? selectedTerms[0] : '';
          this.realFilter.value = newValue;
        }

        // Trigger change event
        const event = new Event('change', {bubbles: true});
        this.realFilter.dispatchEvent(event);
      }
      else {
        // Original checkbox handling
        const realCheckboxes = this.realFilter.querySelectorAll('input[type="checkbox"]');

        realCheckboxes.forEach(checkbox => {
          const value = checkbox.value;
          checkbox.checked = selectedTerms.includes(value);

          // Trigger change event to ensure any attached handlers are notified
          const event = new Event('change', {bubbles: true});
          checkbox.dispatchEvent(event);
        });
      }

    }

    /**
     * Sync pseudo filter with real filter values.
     */
    syncWithRealFilter() {
      if (!this.realFilter) {
        return;
      }

      let checkedValues = [];

      // Check if real filter is a SELECT element
      if (this.realFilter.tagName === 'SELECT') {
        if (this.realFilter.multiple) {
          checkedValues = Array.from(this.realFilter.selectedOptions).map(opt => opt.value);
        }
        else {
          checkedValues = this.realFilter.value ? [this.realFilter.value] : [];
        }
      }
      else {
        // Original checkbox handling
        const realCheckboxes = this.realFilter.querySelectorAll('input[type="checkbox"]:checked');
        checkedValues = Array.from(realCheckboxes).map(cb => cb.value);
      }

      // Update pseudo filter checkboxes based on real filter values
      let pseudoUpdates = [];
      this.allCheckboxes.forEach(checkbox => {
        const wasChecked = checkbox.checked;
        checkbox.checked = checkedValues.includes(checkbox.value);
        if (wasChecked !== checkbox.checked) {
          pseudoUpdates.push({
            termId: checkbox.value,
            wasChecked: wasChecked,
            nowChecked: checkbox.checked
          });
        }
      });

    }

    /**
     * Handle form submission.
     *
     * @param {Event} event - The submit event.
     */
    handleFormSubmit(event) {
      // Ensure real filter has correct values before submission.
      const selectedTerms = this.getSelectedTermsWithChildren();
      this.updateRealFilter(selectedTerms);
    }

    /**
     * Destroy the handler and clean up resources.
     */
    destroy() {
      // Clear any pending timeouts
      if (this.submitTimeout) {
        clearTimeout(this.submitTimeout);
        this.submitTimeout = null;
      }

      // Clear hover close timer
      if (this.hoverCloseTimer) {
        clearTimeout(this.hoverCloseTimer);
        this.hoverCloseTimer = null;
      }

      // Remove all event listeners
      this.removeEventListeners();

      // Clear references
      this.wrapper = null;
      this.realFilter = null;
      this.exposedForm = null;
      this.allCheckboxes = null;
      this.termMap.clear();
      this.childToParentMap.clear();
    }

  }

  // Export the base class to window for plugin files to extend
  window.PseudoFilterHandler = PseudoFilterHandler;

})(jQuery, Drupal, once, window.dataLayer);
